/**
 * RTWO Indexer Engine — Block Indexing Service
 */

import { INDEXER_SERVICE_IDS } from '../domain-models';
import type { IndexedBlockId, RTWOIndexedBlock } from '../domain-models';
import type { BlockIndexResult, IBlockIndexingService } from '../service-interfaces';
import type { IndexerRepositories } from '../repositories/indexer-repositories';
import type { ProtocolAdapterRegistry } from '../registry/adapter-registries';
import { IndexerInternalEventTypes } from '../events/indexer-event-bus';
import { BaseIndexerService, type IndexerServiceContext } from './base-indexer-service';

export class BlockIndexingService extends BaseIndexerService implements IBlockIndexingService {
  readonly serviceId = INDEXER_SERVICE_IDS.BLOCK_INDEXING;
  readonly serviceName = 'Block Indexing Service';
  readonly description = 'Indexes blocks via Protocol adapter. No fake blocks indexed.';
  readonly integrations = ['RPC Platform', 'Ledger Engine', 'Node Infrastructure'];
  readonly requiresRPCSource = true;
  readonly requiresIndexStorage = false;

  constructor(
    context: IndexerServiceContext,
    private readonly repositories: IndexerRepositories,
    private readonly protocolRegistry: ProtocolAdapterRegistry
  ) {
    super(context);
  }

  async indexBlock(blockNumber: number): Promise<BlockIndexResult> {
    if (!this.protocolRegistry.isConfigured()) {
      return {
        success: false,
        indexedBlockId: null,
        error:
          'RPC Platform Not Connected — Cannot index blocks until real RPC source is connected.',
        isPlaceholder: true,
        source: 'Indexer Engine — RPC Platform Not Connected',
      };
    }

    const adapterResult = await this.protocolRegistry.getActiveAdapter().indexBlock(blockNumber);

    if (adapterResult?.block) {
      this.repositories.blocks.save(adapterResult.block);
      this.context.eventBus.emit(IndexerInternalEventTypes.BLOCK_INDEXED, {
        indexedBlockId: adapterResult.block.indexedBlockId,
        blockNumber,
      });
    }

    return {
      success: false,
      indexedBlockId: null,
      error: 'RPC Platform Not Connected — Cannot index blocks until real RPC source is connected.',
      isPlaceholder: true,
      source: 'Indexer Engine — RPC Platform Not Connected',
    };
  }

  getAllIndexedBlocks(): RTWOIndexedBlock[] {
    if (!this.protocolRegistry.isConfigured()) {
      return [];
    }

    return this.repositories.blocks.findAll();
  }

  getIndexedBlock(indexedBlockId: IndexedBlockId): RTWOIndexedBlock | null {
    if (!this.protocolRegistry.isConfigured()) {
      return null;
    }

    return this.repositories.blocks.findById(indexedBlockId);
  }

  getIndexedBlockCount(): 0 {
    return 0;
  }

  getLatestIndexedBlockNumber(): null {
    return null;
  }
}
